import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import ClientForm from '../molecules/ClientForm'
import ChevronRightSvg from '../other/svg/ChevronRightSvg'
import { FETCH_CLIENT, UPDATE_CLIENT_DETAILS } from 'src/redux/clientReducer'
import { RootReducer } from 'src/redux/rootReducer'
import { ClientFormData } from 'src/helpers/clientFormValidation'

const ClientEdit = (): JSX.Element => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const loading = useSelector((state: RootReducer) => state.clientReducer.loading)
  const client = useSelector((state: RootReducer) => state.clientReducer.client[id ?? ''] ?? null)

  useEffect(() => {
    if (id !== null) {
      dispatch(FETCH_CLIENT(id ?? ''))
    }
  }, [dispatch, id])

  const handleCancel = (): void => {
    navigate('/selling/clients')
  }

  const onSubmit = (values: ClientFormData): void => {
    if (client !== null) {
      dispatch(
        UPDATE_CLIENT_DETAILS({
          id: id ?? '',
          data: values
        })
      )
    }
  }

  const header = (
    <div className="self-stretch min-h-14 bg-Estigroup-primary rounded-lg border border-neutral-200 flex flex-col justify-start items-center">
      <div className="self-stretch px-4 py-3 inline-flex justify-start items-center gap-3">
        <button
          type="button"
          onClick={handleCancel}
          className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
          {t('clients.title')}
        </button>
        <ChevronRightSvg className="size-3.5 text-foregrounds-fg-on-color" />
        <div className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
          {t('clients.edit_client')}
        </div>
      </div>
    </div>
  )

  if (loading || client === null) {
    return (
      <Layout withoutToolbar>
        <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
          {header}
          <div className="self-stretch flex-1 flex justify-center items-center text-foregrounds-fg-muted text-sm font-medium font-Montserrat">
            {loading ? t('my_vehicles.loading_offers') : t('clients.empty_subtitle')}
          </div>
        </div>
      </Layout>
    )
  }

  const initialValues: ClientFormData = {
    email: client.email ?? '',
    first_name: client.first_name ?? '',
    last_name: client.last_name ?? '',
    country: client.country ?? '',
    place: client.place ?? '',
    street: client.street ?? '',
    house_number: client.house_number ?? '',
    zip_code: client.zip_code ?? '',
    phone_number: client.phone_number ?? '',
    email_address: client.email_address ?? '',
    company_name: client.company_name ?? '',
    client_type: client.client_type ?? 'company',
    tax_number: client.tax_number ?? '',
    vat_number: client.vat_number ?? '',
    id_number: client.id_number ?? '',
    document_number: client.document_number ?? ''
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        {header}
        <ClientForm
          initialValues={initialValues}
          onSubmit={onSubmit}
          onCancel={handleCancel}
          mode="edit"
          loading={loading}
        />
      </div>
    </Layout>
  )
}

export default ClientEdit
